(function () {
  let feedbackCart = null;
  let cartReadVersion = 0;
  try {
    const initialCart = JSON.parse(document.getElementById('WholesaleFeedbackCart')?.textContent || 'null');
    if (Array.isArray(initialCart?.items)) feedbackCart = initialCart;
  } catch {}

  function rememberFeedbackCart(cart) {
    feedbackCart = cart;
    document.dispatchEvent(new CustomEvent('paradisia:cart-quantities-ready'));
  }

  function getKnownVariantQuantity(variantId) {
    return getVariantQuantity(feedbackCart, variantId);
  }

  function normalisePositiveInteger(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function floorToLot(value, lot) {
    const safeValue = Math.max(0, parseInt(value, 10) || 0);
    const safeLot = normalisePositiveInteger(lot, 1);
    return Math.floor(safeValue / safeLot) * safeLot;
  }

  function getVariantQuantity(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || !variantId) return 0;

    return cart.items.reduce(function (total, item) {
      if (String(item.variant_id || item.id) !== String(variantId)) return total;
      return total + (parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  function getValidTotal(quantity, rules) {
    const lot = normalisePositiveInteger(rules && rules.lot, 1);
    const min = normalisePositiveInteger(rules && rules.min, lot);
    const max = rules && Number.isFinite(rules.max) ? Math.max(0, rules.max) : Infinity;
    let validTotal = floorToLot(quantity, lot);

    if (Number.isFinite(max)) validTotal = Math.min(validTotal, floorToLot(max, lot));
    if (validTotal > 0 && validTotal < min) return 0;
    return validTotal;
  }

  function getRemaining(max, existingQuantity, lot, min) {
    if (!Number.isFinite(max)) return Infinity;

    const remaining = floorToLot(Math.max(0, max - existingQuantity), lot);
    const requiredAddition = getRequiredAddition(existingQuantity, lot, min);
    return remaining >= requiredAddition ? remaining : 0;
  }

  function getRequiredAddition(existingQuantity, lot, min) {
    const safeLot = normalisePositiveInteger(lot, 1);
    const safeMin = normalisePositiveInteger(min, safeLot);
    const existing = Math.max(0, parseInt(existingQuantity, 10) || 0);
    const minimumDeficit = Math.max(0, safeMin - existing);
    return Math.max(safeLot, Math.ceil(minimumDeficit / safeLot) * safeLot);
  }

  async function fetchCart() {
    const version = ++cartReadVersion;
    const response = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    if (version === cartReadVersion && Array.isArray(cart?.items)) rememberFeedbackCart(cart);
    return cart;
  }

  async function changeCartLine(lineKey, quantity) {
    const version = ++cartReadVersion;
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    });

    const responseText = await response.text();
    let cart = null;

    try {
      cart = responseText ? JSON.parse(responseText) : null;
    } catch (error) {
      throw new Error('Unable to read corrected cart');
    }

    if (!response.ok || !cart || !Array.isArray(cart.items)) {
      throw new Error('Unable to correct cart quantity');
    }

    if (version === cartReadVersion) rememberFeedbackCart(cart);
    return cart;
  }

  async function reconcileVariantCart(options) {
    const variantId = options && options.variantId;
    const rules = (options && options.rules) || {};
    let cart = (options && options.cart) || (await fetchCart());
    const observedQuantity = getVariantQuantity(cart, variantId);
    const targetQuantity = getValidTotal(observedQuantity, rules);
    let repaired = false;
    let guard = 0;

    while (getVariantQuantity(cart, variantId) > targetQuantity && guard < 100) {
      const currentTotal = getVariantQuantity(cart, variantId);
      const matchingItems = cart.items.filter(function (item) {
        return String(item.variant_id || item.id) === String(variantId);
      });
      const line = matchingItems[matchingItems.length - 1];

      if (!line || !line.key) throw new Error('Unable to identify cart line');

      const reduction = Math.min(parseInt(line.quantity, 10) || 0, currentTotal - targetQuantity);
      const nextLineQuantity = Math.max(0, (parseInt(line.quantity, 10) || 0) - reduction);
      cart = await changeCartLine(line.key, nextLineQuantity);
      repaired = true;
      guard += 1;
    }

    if (getVariantQuantity(cart, variantId) !== targetQuantity) {
      throw new Error('Unable to verify corrected cart quantity');
    }

    return {
      cart: cart,
      observedQuantity: observedQuantity,
      finalQuantity: targetQuantity,
      repaired: repaired,
    };
  }

  function getLimitMessage(rules, existingQuantity) {
    const remaining = getRemaining(rules.max, existingQuantity, rules.lot, rules.min);
    if (!rules.stockLimited) {
      return 'Maximum for this product is ' + floorToLot(rules.max, rules.lot) + '.';
    }
    if (existingQuantity > 0 && remaining === 0) {
      return 'All available stock is already in cart';
    }
    return 'Only ' + remaining + ' units available.';
  }

  function refreshFeedbackCart() {
    fetchCart().catch(function () {});
  }
  document.addEventListener('paradisia:cart-updated', refreshFeedbackCart);
  window.addEventListener('focus', refreshFeedbackCart);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshFeedbackCart();
  });

  function showQuantityFeedback(options) {
    const { input, variantId, rules, requestedQty, fallback, showMessage } = options;
    if (input.disabled) return;
    let message = fallback;
    if (Number.isFinite(rules.max)) {
      if (feedbackCart) {
        const existing = getVariantQuantity(feedbackCart, variantId);
        if (requestedQty > getRemaining(rules.max, existing, rules.lot, rules.min)) {
          message = getLimitMessage(rules, existing);
        }
      } else {
        message = 'The cart could not be verified. Please refresh and review your cart.';
      }
    }
    showMessage(message);
  }

  window.ParadisiaWholesaleQuantity = {
    getKnownVariantQuantity: getKnownVariantQuantity,
    getLimitMessage: getLimitMessage,
    showQuantityFeedback: showQuantityFeedback,
    fetchCart: fetchCart,
    floorToLot: floorToLot,
    getRemaining: getRemaining,
    getRequiredAddition: getRequiredAddition,
    getValidTotal: getValidTotal,
    getVariantQuantity: getVariantQuantity,
    reconcileVariantCart: reconcileVariantCart,
  };
})();
